import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CoreSessionStorage } from '../../../storage/session/CoreSessionStorage';
import { getIsLanguageRTL } from '../../../utils/language';
import { fetchPlayerConfigAction } from '../../apiActions/fetchPlayerConfigAction';

import type { ChannelsType, ISettingsInitialState, VolumeChannelType } from './types';
import { StreamQuality } from './types';

const initialState: ISettingsInitialState = {
  isInFullscreen: false,
  isGameVisible: true,
  isSecondBetBoardToggle: true,
  isAnimationToggle: false,
  stream: {
    isEnabled: true,
    isEnabledBySystem: true,
    isVideoPlaying: false,
    quality: StreamQuality.Auto,
    qualityLimit: 0, //StreamQuality.High
    streamLatency: 1000,
  },
  // TODO: Need to refactor this channel disabling
  volume: {
    restored: false,
    master: {
      value: 0,
      temporaryValue: null,
      previousValue: CoreSessionStorage.getLastPositiveMasterVolume() ?? 1,
    },
    channels: {},
    uiChannel: null,
  },
  language: {},
  showOtherMessages: true,
};

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setIsInFullscreen: (state, action: PayloadAction<boolean>) => {
      state.isInFullscreen = action.payload;
    },
    /* Stream */
    // Listened by settingSyncMiddleware
    setStreamEnabled: (state, action: PayloadAction<boolean>) => {
      state.stream.isEnabled = action.payload;
    },
    // Flag used for managing stream on inactivity
    setStreamEnabledBySystem: (state, action: PayloadAction<boolean>) => {
      state.stream.isEnabledBySystem = action.payload;
    },
    setIsVideoPlaying: (state, action: PayloadAction<boolean>) => {
      state.stream.isVideoPlaying = action.payload;
    },
    setStreamQualityLimit: (state, action: PayloadAction<number>) => {
      state.stream.qualityLimit = action.payload;
    },
    setStreamQuality: (state, action: PayloadAction<StreamQuality>) => {
      state.stream.quality = action.payload;
    },
    setStreamLatency: (state, action: PayloadAction<number>) => {
      state.stream.streamLatency = action.payload;
    },
    isAnimationToggle: (state) => {
      state.isAnimationToggle = !state.isAnimationToggle;
    },
    isSecondBetBoardToggle: (state) => {
      state.isSecondBetBoardToggle = !state.isSecondBetBoardToggle;
    },

    /* Chat */
    setShowOtherMessages: (state, action: PayloadAction<boolean>) => {
      state.showOtherMessages = action.payload;
    },

    /* Volume */
    setGameVolumeChannels: (state, action: PayloadAction<string[]>) => {
      const newChannels = action.payload.reduce(
        (acc, channel) => {
          if (state.volume.channels[channel]) return acc;
          acc[channel] = {
            value: 0,
            temporaryValue: null,
            previousValue: 1,
          };
          return acc;
        },
        {} as { [key: string]: VolumeChannelType }
      );
      state.volume.channels = {
        ...state.volume.channels,
        ...newChannels,
      };
    },

    setMasterVolumeOnly: (state, action: PayloadAction<number>) => {
      setMasterVolumeFunc(state, action.payload);
    },

    toggleMasterVolume: (state) => {
      const newVolume = state.volume.master.value ? 0 : state.volume.master.previousValue;
      setMasterVolumeFunc(state, newVolume);
    },

    mute: (state) => {
      setMasterVolumeFunc(state, 0);
    },

    unmute: (state) => {
      setMasterVolumeFunc(state, state.volume.master.previousValue);
    },

    // Listened by settingSyncMiddleware
    setMasterVolume: (state, action: PayloadAction<number>) => {
      setMasterVolumeFunc(state, action.payload);
    },
    setMasterVolumeTemporary: (state, action: PayloadAction<number | null>) => {
      state.volume.master.temporaryValue = action.payload;
    },

    setAllVolumes: (state, action: PayloadAction<number>) => {
      state.volume.restored = true;
      Object.keys(state.volume.channels).forEach((key) => {
        if (!state.volume.channels[key]) return;
        state.volume.channels[key].value = action.payload;
      });
    },

    // Listened by settingSyncMiddleware
    setVolume: (
      state,
      action: PayloadAction<{ channel: keyof (typeof initialState)['volume']['channels']; value: number }>
    ) => setVolumeValue(state, action),

    // Avoid redundant savePlayerConfigGeneric API call on iOS devices in VolumeRestorer
    setVolumeIOSToggle: (
      state,
      action: PayloadAction<{ channel: keyof (typeof initialState)['volume']['channels']; value: number }>
    ) => setVolumeValue(state, action),

    setVolumeTemporary: (
      state,
      action: PayloadAction<{ channel: keyof (typeof initialState)['volume']['channels']; value: number | null }>
    ) => {
      const { channel, value } = action.payload;
      if (state.volume.channels[channel]) {
        state.volume.channels[channel].temporaryValue = value;
      } else {
        throw new Error(`Volume channel ${channel} does not exist`);
      }
    },
    setUiVolumeChannel: (state, action: PayloadAction<string | null>) => {
      state.volume.uiChannel = action.payload;
    },
    setNoVolumeControl: (state, action: PayloadAction<boolean>) => {
      state.volume.noVolumeControl = action.payload;
    },
    /* Language */
    setLanguage: (state, action: PayloadAction<string>) => {
      state.language.current = action.payload;
      state.language.isRTL = getIsLanguageRTL(state.language.current);
    },
    setIsGameVisible: (state, action: PayloadAction<boolean>) => {
      state.isGameVisible = action.payload;
    },
    setIsStudioSoundDisabled: (state, action: PayloadAction<boolean>) => {
      state.isStudioSoundDisabled = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPlayerConfigAction.fulfilled, (state, action) => {
      state.stream.isEnabled = action.payload.videoStream;
      const { disableStudioSound } = action.payload.compliance;

      // TODO: default channels should be mapped via game config
      if (state.volume.channels['studio'] && !disableStudioSound) {
        state.volume.channels['studio'].value = action.payload.videoVolume;
        state.volume.channels['studio'].previousValue = action.payload.videoVolume;
      }
      if (state.volume.channels['game']) {
        state.volume.channels['game'].value = action.payload.gameEffectsVolume;
        state.volume.channels['game'].previousValue = action.payload.gameEffectsVolume;
      }
      if (state.volume.channels['background'] && action.payload.backgroundMusicVolume) {
        state.volume.channels['background'].value = action.payload.backgroundMusicVolume;
        state.volume.channels['background'].previousValue = action.payload.backgroundMusicVolume;
      }
      if (state.volume.channels['voiceover'] && action.payload.voiceOverVolume) {
        state.volume.channels['voiceover'].value = action.payload.voiceOverVolume;
        state.volume.channels['voiceover'].previousValue = action.payload.voiceOverVolume;
      }
      state.showOtherMessages = action.payload.showOtherMessages;
      state.isAnimationToggle = action.payload.hfAnimationEnabled;
      state.isSecondBetBoardToggle = action.payload.isSecondBetboardEnabled;
      state.isStudioSoundDisabled = disableStudioSound ?? false;
    });
  },
});

// UTILS
const isAllChannelsHaveVolume = (channels: ChannelsType): boolean => {
  return Object.keys(channels).some((key) => channels[key]?.value > 0);
};

const setMasterVolumeFunc = (state: ISettingsInitialState, newVolume: number): void => {
  state.volume.restored = true;
  state.volume.master.value = newVolume;

  if (state.volume.noVolumeControl) {
    updateChannelsWithNoVolumeControl(state, newVolume);
  } else if (!isAllChannelsHaveVolume(state.volume.channels)) {
    updateChannelsWithPreviousVolume(state);
  }

  updateMasterVolume(state, newVolume);
};

const updateChannelsWithNoVolumeControl = (state: ISettingsInitialState, newVolume: number): void => {
  Object.keys(state.volume.channels).forEach((key) => {
    if (!state.stream.isEnabled && key === 'studio' && newVolume > 0) return;

    // For Brazilian jurisdiction -> disable studio sound if dedicated language is not 'pt'
    if (state.isStudioSoundDisabled && key === 'studio') return;

    if (!state.volume.channels[key]) throw new Error(`Volume channel ${key} does not exist`);
    state.volume.channels[key].value = newVolume;
    state.volume.channels[key].previousValue = newVolume;
  });
};

const updateChannelsWithPreviousVolume = (state: ISettingsInitialState): void => {
  Object.keys(state.volume.channels).forEach((key) => {
    if (state.volume.noVolumeControl && !state.stream.isEnabled && key === 'studio') return;

    // For Brazilian jurisdiction -> disable studio sound if dedicated language is not 'pt'
    if (state.isStudioSoundDisabled && key === 'studio') return;

    if (!state.volume.channels[key]) throw new Error(`Volume channel ${key} does not exist`);

    const newValues = state.volume.channels[key].previousValue > 0 ? state.volume.channels[key].previousValue : 1;

    state.volume.channels[key].value = newValues;
    state.volume.channels[key].previousValue = newValues;
  });
};

const updateMasterVolume = (state: ISettingsInitialState, newVolume: number): void => {
  if (newVolume) {
    state.volume.master.previousValue = newVolume;
    CoreSessionStorage.setLastPositiveMasterVolume(newVolume);
  }

  state.volume.master.temporaryValue = null;
  CoreSessionStorage.setLastMasterVolume(newVolume);
};

const setVolumeValue = (
  state: ISettingsInitialState,
  action: PayloadAction<{ channel: keyof (typeof initialState)['volume']['channels']; value: number }>
): void => {
  const { channel, value } = action.payload;
  state.volume.restored = true;

  if (state.volume.channels[channel]) {
    updateChannelVolume(state, channel, value);
    checkAndUpdateMasterVolume(state, value);
  } else {
    throw new Error(`Volume channel ${channel} does not exist`);
  }
};

const updateChannelVolume = (
  state: ISettingsInitialState,
  channel: keyof (typeof initialState)['volume']['channels'],
  value: number
): void => {
  const channelState = state.volume.channels[channel];
  if (!channelState) return;
  channelState.value = value;
  if (value) {
    channelState.previousValue = value;
  }
  channelState.temporaryValue = null;
};

const checkAndUpdateMasterVolume = (state: ISettingsInitialState, value: number): void => {
  if (value === 0 && !isAllChannelsHaveVolume(state.volume.channels)) {
    state.volume.master.value = 0;
  }
};

export const settingsActions = settingsSlice.actions;
